import type { ConfigurationContextValue } from "../root/ConfigurationContext";
import type { Deal } from "../types";

/**
 * Canonical project lifecycle values. Keep this list in the UI layer as a
 * guardrail for stored configuration and imported records that may still
 * contain one of the pre-pipeline stage names.
 */
export const PROJECT_STAGE_VALUES = [
  "opportunity",
  "estimate-sent",
  "in-progress",
  "closed-lost",
  "on-hold",
  "completed-pending-payment",
  "completed",
  "referral-mining",
] as const;

export type ProjectStageValue = (typeof PROJECT_STAGE_VALUES)[number];

export const ACTIVE_PROJECT_STAGE_VALUES = [
  "opportunity",
  "estimate-sent",
  "in-progress",
  "on-hold",
  "completed-pending-payment",
  "referral-mining",
] as const satisfies readonly ProjectStageValue[];

export const TERMINAL_PROJECT_STAGE_VALUES = [
  "closed-lost",
  "completed",
] as const satisfies readonly ProjectStageValue[];

export const isProjectStage = (
  stage: string | null | undefined,
): stage is ProjectStageValue =>
  stage != null && (PROJECT_STAGE_VALUES as readonly string[]).includes(stage);

export const isActiveProjectStage = (stage: string | null | undefined) =>
  stage != null &&
  (ACTIVE_PROJECT_STAGE_VALUES as readonly string[]).includes(stage);

export type DealsByStage = Record<Deal["stage"], Deal[]>;

export const getDealsByStage = (
  unorderedDeals: Deal[],
  dealStages: ConfigurationContextValue["projectStages"],
) => {
  if (!dealStages) return {};
  const dealsByStage: Record<Deal["stage"], Deal[]> = unorderedDeals.reduce(
    (acc, deal) => {
      // if deal has a stage that does not exist in configuration, assign it to the first stage
      const stage = dealStages.find((s) => s.value === deal.stage)
        ? deal.stage
        : dealStages[0].value;
      acc[stage].push(deal);
      return acc;
    },
    dealStages.reduce(
      (obj, stage) => ({ ...obj, [stage.value]: [] }),
      {} as Record<Deal["stage"], Deal[]>,
    ),
  );
  // order each column by index
  dealStages.forEach((stage) => {
    dealsByStage[stage.value] = dealsByStage[stage.value].sort(
      (recordA: Deal, recordB: Deal) => recordA.index - recordB.index,
    );
  });
  return dealsByStage;
};
